import { _ as _define_property } from "@swc/helpers/_/_define_property";
import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { InvoiceIntegrationStatusEnum, InvoiceIntegrationStatusDTO } from 'anchor-scout-common/types';
import { IntegrationFailureModel } from './IntegrationFailureModel';
export var InvoiceIntegrationStatus = /*#__PURE__*/ function(InvoiceIntegrationStatus) {
    InvoiceIntegrationStatus["PendingSync"] = "pendingSync";
    InvoiceIntegrationStatus["InvoiceSyncFailed"] = "invoiceSyncFailed";
    InvoiceIntegrationStatus["PaymentSyncFailed"] = "paymentSyncFailed";
    InvoiceIntegrationStatus["RefundSyncFailed"] = "refundSyncFailed";
    InvoiceIntegrationStatus["InvoiceRecorded"] = "invoiceRecorded";
    InvoiceIntegrationStatus["VoidedRecorded"] = "voidedRecorded";
    InvoiceIntegrationStatus["RefundRecorded"] = "refundRecorded";
    InvoiceIntegrationStatus["PaymentRecorded"] = "paymentRecorded";
    return InvoiceIntegrationStatus;
}({});
export class InvoiceIntegrationModel {
    static fromDTO(dto, isZeroAmount) {
        switch(dto.status){
            case InvoiceIntegrationStatusDTO.Synced:
                return new InvoiceIntegrationModel({
                    status: "invoiceRecorded",
                    date: dto.synced.date
                });
            case InvoiceIntegrationStatusDTO.SyncFailed:
                return new InvoiceIntegrationModel({
                    status: "invoiceSyncFailed",
                    failure: new IntegrationFailureModel(dto.syncFailed)
                });
            case InvoiceIntegrationStatusDTO.PaymentFailed:
                return new InvoiceIntegrationModel({
                    status: "paymentSyncFailed",
                    failure: new IntegrationFailureModel(dto.paymentFailed)
                });
            case InvoiceIntegrationStatusDTO.PendingSync:
                {
                    const { customerLinked, itemsLinked } = dto.pendingSync;
                    return new InvoiceIntegrationModel({
                        status: "pendingSync",
                        customerLinked,
                        itemsLinked
                    });
                }
            case InvoiceIntegrationStatusDTO.Voided:
                return new InvoiceIntegrationModel({
                    status: "voidedRecorded",
                    date: dto.voided.date
                });
            case InvoiceIntegrationStatusDTO.Paid:
                return new InvoiceIntegrationModel({
                    status: isZeroAmount ? "invoiceRecorded" : "paymentRecorded",
                    date: dto.paid.date
                });
            case InvoiceIntegrationStatusDTO.Refunded:
                return new InvoiceIntegrationModel({
                    status: "refundRecorded",
                    date: dto.refunded.date
                });
            case InvoiceIntegrationStatusDTO.RefundFailed:
                return new InvoiceIntegrationModel({
                    status: "refundSyncFailed",
                    failure: new IntegrationFailureModel(dto.refundFailed)
                });
        }
    }
    static fromAccountingDTO(dto, isZeroAmount) {
        const accountingIntegrations = dto.map((integration)=>({
                type: integration.type,
                integration: InvoiceIntegrationModel.fromDTO(integration.value, isZeroAmount)
            }));
        return accountingIntegrations;
    }
    static fromScoutDTO(dto) {
        switch(dto.status){
            case InvoiceIntegrationStatusEnum.InvoiceRecorded:
                return new InvoiceIntegrationModel({
                    status: "invoiceRecorded",
                    date: dto.date
                });
            case InvoiceIntegrationStatusEnum.InvoiceSyncFailed:
                return new InvoiceIntegrationModel({
                    status: "invoiceSyncFailed",
                    failure: IntegrationFailureModel.fromScoutDTO(dto.date, dto.customFailure)
                });
            case InvoiceIntegrationStatusEnum.PaymentSyncFailed:
                return new InvoiceIntegrationModel({
                    status: "paymentSyncFailed",
                    failure: IntegrationFailureModel.fromScoutDTO(dto.date, dto.customFailure)
                });
            case InvoiceIntegrationStatusEnum.PendingSync:
                return new InvoiceIntegrationModel({
                    status: "pendingSync",
                    customerLinked: false,
                    itemsLinked: false
                });
            case InvoiceIntegrationStatusEnum.VoidedRecorded:
                return new InvoiceIntegrationModel({
                    status: "voidedRecorded",
                    date: dto.date
                });
            case InvoiceIntegrationStatusEnum.PaymentRecorded:
                return new InvoiceIntegrationModel({
                    status: "paymentRecorded",
                    date: dto.date
                });
            case InvoiceIntegrationStatusEnum.RefundRecorded:
                return new InvoiceIntegrationModel({
                    status: "refundRecorded",
                    date: dto.date,
                    numRefunds: dto.numRefunds
                });
            case InvoiceIntegrationStatusEnum.RefundSyncFailed:
                return new InvoiceIntegrationModel({
                    status: "refundSyncFailed",
                    failure: IntegrationFailureModel.fromScoutDTO(dto.date, dto.customFailure)
                });
        }
    }
    isSynced() {
        return [
            "invoiceRecorded",
            "voidedRecorded",
            "paymentRecorded",
            "refundRecorded"
        ].includes(this.data.status);
    }
    status() {
        return this.data.status;
    }
    isInvoiceRecorded() {
        return this.data.status === "invoiceRecorded";
    }
    isInvoiceSyncFailed() {
        return this.data.status === "invoiceSyncFailed";
    }
    isSyncFailed() {
        return [
            "invoiceSyncFailed",
            "paymentSyncFailed",
            "refundSyncFailed"
        ].includes(this.data.status);
    }
    isPendingSync() {
        return this.data.status === "pendingSync";
    }
    isPendingCustomerLinked() {
        return this.data.status === "pendingSync" && !this.data.customerLinked;
    }
    isPendingItemsLinked() {
        return this.data.status === "pendingSync" && !this.data.itemsLinked;
    }
    isPaymentSyncFailed() {
        return this.data.status === "paymentSyncFailed";
    }
    isRefundSyncFailed() {
        return this.data.status === "refundSyncFailed";
    }
    isPaymentRecorded() {
        return this.data.status === "paymentRecorded";
    }
    isRefundRecorded() {
        return this.data.status === "refundRecorded";
    }
    accept(visitor) {
        switch(this.data.status){
            case "invoiceRecorded":
                return visitor.whenInvoiceRecorded(this.data);
            case "voidedRecorded":
                return visitor.whenVoidedRecorded(this.data);
            case "invoiceSyncFailed":
                return visitor.whenInvoiceSyncFailed(this.data);
            case "paymentSyncFailed":
                return visitor.whenPaymentSyncFailed(this.data);
            case "pendingSync":
                return visitor.whenPendingSync(this.data);
            case "paymentRecorded":
                return visitor.whenPaymentRecorded(this.data);
            case "refundRecorded":
                return visitor.whenRefundRecorded(this.data);
            case "refundSyncFailed":
                return visitor.whenRefundSyncFailed(this.data);
        }
    }
    setNumRefunds(numRefunds) {
        if (this.data.status === "refundRecorded") {
            return new InvoiceIntegrationModel(_object_spread_props(_object_spread({}, this.data), {
                numRefunds
            }));
        }
        return this;
    }
    constructor(data){
        _define_property(this, "data", void 0);
        this.data = data;
    }
}
